/**
 * @file AdditionalUsersList.js
 * @description Displays a list of additional users with empty and populated states.
 */

import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './AdditionalUsers.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { getAdditionalUsers } from '../../services/userService';
import { ROUTES } from '../../constants/routes';
import { relationshipMap } from '../../utils/data/enums';
import { getStatusClass, getTenantUserStatusName } from '../../utils/utils';
import { getText } from '../../i18n';

const BackNavIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        viewBox="0 0 30 30"
        fill="none"
    >
        <g clipPath="url(#clip0_au_back)">
            <path
                d="M12.5 27.5L0 15L12.5 2.5L14.7188 4.71875L4.4375 15L14.7188 25.2813L12.5 27.5Z"
                fill="#2F2B43"
            />
        </g>
        <defs>
            <clipPath id="clip0_au_back">
                <rect width="30" height="30" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const AdditionalUsersList = () => {
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [additionalUsers, setAdditionalUsers] = useState([]);

    useEffect(() => {
        loadAdditionalUsers();
    }, []);

    const loadAdditionalUsers = async () => {
        try {
            setIsLoading(true);
            const users = await getAdditionalUsers();
            setAdditionalUsers(users);
        } catch (error) {
            console.error('Failed to fetch additional users', error);
            if (error?.response?.data?.detail) {
                Toast.error(error?.response?.data?.detail);
            } else {
                Toast.error(getText('msg_6903d70973ab'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddUser = () => {
        if (window.innerWidth < 576) {
            navigate(ROUTES.ADDITIONAL_USERS_ADD);
            return;
        }
        showModal({
            type: ModalTypes.ADDITIONAL_USER_FORM,
            onSuccess: () => {
                loadAdditionalUsers();
            },
        });
    };

    const handleViewUser = userId => {
        if (window.innerWidth < 576) {
            navigate(ROUTES.ADDITIONAL_USERS_VIEW.replace(':userId', userId));
            return;
        }
        showModal({
            type: ModalTypes.ADDITIONAL_USER_FORM,
            userId: userId,
            onSuccess: () => {
                loadAdditionalUsers();
            },
        });
    };

    const getRelationshipName = relationship_id => {
        return relationshipMap[relationship_id] || 'Unknown';
    };

    const renderUserCards = () => (
        <div className={styles.cardList}>
            {additionalUsers.map(user => (
                <div key={user.id} className={styles.userCard}>
                    <div className={styles.cardHeader}>
                        <span className={styles.cardName}>
                            {user.first_name} {user.last_name}
                        </span>
                        <button
                            type="button"
                            className={styles.cardEditLink}
                            onClick={() => handleViewUser(user.id)}
                        >
                            {getText('msg_5301648dcf6b')}
                        </button>
                    </div>
                    <div className={styles.cardRow}>
                        <span className={styles.cardLabel}>{getText('msg_84add5b29527')}</span>
                        <span className={styles.cardValue}>
                            {user.email_address}
                        </span>
                    </div>
                    <div className={styles.cardRow}>
                        <span className={styles.cardLabel}>{getText('msg_9b4a86cba424')}</span>
                        <span className={styles.cardValue}>
                            {getRelationshipName(user.relationship_id)}
                        </span>
                    </div>
                    <div className={styles.cardRow}>
                        <span className={styles.cardLabel}>{getText('msg_bae7d5be7082')}</span>
                        <button
                            className={getStatusClass(
                                user.tenant_user_status_id ?? 0,
                            )}
                        >
                            {getTenantUserStatusName(
                                user.tenant_user_status_id,
                            ) || 'Not Started'}
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderEmptyState = () => {
        return (
            <div className={styles.emptyStateContainer}>
                <NotFoundBox
                    backgroundColor="#f0f4ff"
                    title={getText('msg_889c9585a8cb')}
                    description={getText('msg_3e335cf8a142')}
                    icon="user-add"
                    actionButtonText={getText('msg_61cc55aa0453')}
                    actionButtonIcon="bi bi-plus-lg"
                    onActionButtonClick={handleAddUser}
                />
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="page-content">
                <div className="card">
                    <div className="card-body px-0 py-4">
                        <div className="text-center mt-5">
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="py-3 px-3 px-sm-0 d-flex flex-column flex-sm-row justify-content-between align-items-start">
                        <div className="d-flex flex-column align-items-start mb-2">
                            <div
                                className={`d-flex d-sm-none align-items-center gap-2 mb-2 ${styles.mobileBackNav}`}
                            >
                                <Link to={ROUTES.PROFILE}>
                                    <BackNavIcon />
                                </Link>
                                {getText('msg_d31e71815f1e')}
                            </div>
                            <p className="page-title card-title ms-0 ms-sm-4 mb-0 d-none d-sm-block">
                                {getText('msg_d31e71815f1e')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1 ms-0 ms-sm-4"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_fa5f25ece768')}
                            </p>
                        </div>
                        {additionalUsers.length > 0 && (
                            <button
                                type="button"
                                onClick={handleAddUser}
                                className="ms-3 me-4 align-self-end button-base ing-button-primary-left align-middle flex-shrink-0"
                            >
                                <i className="bi bi-plus-lg me-1" />
                                {getText('msg_61cc55aa0453')}
                            </button>
                        )}
                    </div>

                    {additionalUsers.length === 0 ? (
                        renderEmptyState()
                    ) : (
                        <>
                            <div className="d-none d-sm-block">
                                <div className="table-responsive-xxl w-100">
                                    <table className="table table-hover align-middle">
                                        <thead className="custom-table-header table-light">
                                            <tr>
                                                <th className="ps-4">
                                                    <span className="table-column-header-text">
                                                        {getText('msg_709a23220f2c')}
                                                    </span>
                                                </th>
                                                <th>
                                                    <span className="table-column-header-text">
                                                        {getText('msg_84add5b29527')}
                                                    </span>
                                                </th>
                                                <th>
                                                    <span className="table-column-header-text">
                                                        {getText('msg_9b4a86cba424')}
                                                    </span>
                                                </th>
                                                <th>
                                                    <span className="table-column-header-text">
                                                        {getText('msg_bae7d5be7082')}
                                                    </span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {additionalUsers.map(user => (
                                                <tr key={user.id}>
                                                    <td
                                                        className="ps-4 pointer-cursor item-page-name"
                                                        onClick={() =>
                                                            handleViewUser(
                                                                user.id,
                                                            )
                                                        }
                                                    >
                                                        {user.first_name}{' '}
                                                        {user.last_name}
                                                    </td>
                                                    <td
                                                        className={
                                                            styles.secondaryTableText
                                                        }
                                                    >
                                                        {user.email_address}
                                                    </td>
                                                    <td
                                                        className={
                                                            styles.secondaryTableText
                                                        }
                                                    >
                                                        {getRelationshipName(
                                                            user.relationship_id,
                                                        )}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className={getStatusClass(
                                                                user.tenant_user_status_id ??
                                                                    0,
                                                            )}
                                                        >
                                                            {getTenantUserStatusName(
                                                                user.tenant_user_status_id,
                                                            ) || 'Not Started'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="d-block d-sm-none">
                                {renderUserCards()}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdditionalUsersList;
